/**
 * Pages-backed chat upload support.
 *
 * Main flow:
 * 1) Request a Pages upload presign from the Talkyn backend
 * 2) PUT the file directly to S3 with progress
 * 3) Finalize the upload so it appears in Pages metadata under `uploads/...`
 * 4) Optionally attach the file inline to `chat.send` for immediate agent access
 */

import type { ChatAttachment } from '../types/protocol';
import { STORAGE_KEYS } from '../utils/constants';
import { readString } from '../utils/storage';
import { getApiUrl } from '../utils/environment';
import { getApiAuthHeaders } from '../utils/auth';

export const GATEWAY_INLINE_ATTACHMENT_MAX_BYTES = 10_000_000;

// Keep browser-originated inline chat attachments below the gateway's large-image
// offload path. Pages remains the source of truth for larger uploads; the chat
// message includes the Pages path so agents can fetch the file through Pages.
export const CHAT_INLINE_ATTACHMENT_MAX_BYTES = 1_000_000;

export function getChatAttachmentInlineMaxBytes(maxBytes = GATEWAY_INLINE_ATTACHMENT_MAX_BYTES): number {
  return Math.max(0, Math.min(maxBytes, CHAT_INLINE_ATTACHMENT_MAX_BYTES));
}

interface PagesUploadPresignResponse {
  uploadUrl: string;
  finalizeToken: string;
  expiresIn: number;
  inlineAttachmentMaxBytes: number;
  upload: {
    uploadId: string;
    path: string;
    filename: string;
    mimeType: string;
    size: number | null;
    bucket: string;
    s3Key: string;
  };
}

interface PagesUploadFinalizeResponse {
  upload: {
    kind: 'upload';
    path: string;
    uploadId: string;
    filename: string;
    mimeType: string;
    size: number;
    visibility: string;
    createdAt: string;
    updatedAt: string;
    s3Key: string;
    bucket: string;
  };
  inlineAttachmentMaxBytes: number;
}

export interface UploadedFileMeta {
  filename: string;
  contentType: string;
  size: number;
  bucket: string;
  key: string;
  path: string;
  uploadId: string;
  publicUrl: string | null;
  inlineAttachmentMaxBytes: number;
}

export interface ChatUploadBatchMessageItem {
  filename: string;
  path: string;
  publicUrl?: string | null;
  attachedInline: boolean;
  inlineAttachmentMaxBytes: number;
}

export interface UploadProgress {
  filename: string;
  loaded: number;
  total: number;
  percent: number;
}

function stripTrailingSlashes(url: string): string {
  return url.replace(/\/+$/, '');
}

function guessExtensionFromMime(mime: string): string {
  if (!mime) return '';
  const m = mime.toLowerCase();
  if (m === 'image/png') return 'png';
  if (m === 'image/jpeg') return 'jpg';
  if (m === 'image/webp') return 'webp';
  if (m === 'image/gif') return 'gif';
  if (m === 'image/svg+xml') return 'svg';
  return '';
}

function encodeKeyForUrl(key: string): string {
  return encodeURIComponent(key).replace(/%2F/g, '/');
}

function buildUploadedFileUrl(meta: Pick<UploadedFileMeta, 'bucket' | 'key'>): string {
  return `https://${meta.bucket}.s3.amazonaws.com/${encodeKeyForUrl(meta.key)}`;
}

export function buildScreenshotFilename(mimeType: string): string {
  const ext = guessExtensionFromMime(mimeType) || 'png';
  return `screenshot-${Date.now()}.${ext}`;
}

export function getUploadBackendConfig(): { backendUrl: string } {
  const backendUrl = readString(STORAGE_KEYS.UPLOAD_BACKEND_URL, getApiUrl());
  return { backendUrl };
}

async function fetchPagesUploadPresign(file: {
  filename: string;
  contentType: string;
  size: number;
}): Promise<PagesUploadPresignResponse> {
  const { backendUrl } = getUploadBackendConfig();
  if (!backendUrl) throw new Error('Upload backend URL not configured');

  const authHeaders = getApiAuthHeaders(STORAGE_KEYS.UPLOAD_AUTH_TOKEN);
  if (!authHeaders.Authorization) {
    throw new Error('Sign in is required for Pages uploads');
  }

  const res = await fetch(`${stripTrailingSlashes(backendUrl)}/api/chat-uploads/presign`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
    },
    body: JSON.stringify(file),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Failed to create Pages upload (${res.status})${text ? `: ${text}` : ''}`);
  }

  return (await res.json()) as PagesUploadPresignResponse;
}

async function finalizePagesUpload(finalizeToken: string): Promise<PagesUploadFinalizeResponse> {
  const { backendUrl } = getUploadBackendConfig();
  if (!backendUrl) throw new Error('Upload backend URL not configured');

  const authHeaders = getApiAuthHeaders(STORAGE_KEYS.UPLOAD_AUTH_TOKEN);
  if (!authHeaders.Authorization) {
    throw new Error('Sign in is required for Pages uploads');
  }

  const res = await fetch(`${stripTrailingSlashes(backendUrl)}/api/chat-uploads/finalize`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
    },
    body: JSON.stringify({ finalizeToken }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Failed to finalize Pages upload (${res.status})${text ? `: ${text}` : ''}`);
  }

  return (await res.json()) as PagesUploadFinalizeResponse;
}

async function putFileWithProgress(
  uploadUrl: string,
  file: File,
  onProgress?: (p: UploadProgress) => void,
): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    xhr.upload.onprogress = (evt) => {
      if (!evt.lengthComputable) return;
      const percent = evt.total ? Math.round((evt.loaded / evt.total) * 100) : 0;
      onProgress?.({
        filename: file.name,
        loaded: evt.loaded,
        total: evt.total,
        percent,
      });
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (${xhr.status})`));
    };

    xhr.onerror = () => reject(new Error('Upload failed (network error)'));
    xhr.send(file);
  });
}

export async function uploadOneFile(
  file: File,
  opts?: { displayName?: string; onProgress?: (p: UploadProgress) => void },
): Promise<UploadedFileMeta> {
  const filename = (opts?.displayName || file.name || `upload-${Date.now()}`).trim();
  const contentType = file.type || 'application/octet-stream';

  const presign = await fetchPagesUploadPresign({
    filename,
    contentType,
    size: file.size,
  });

  await putFileWithProgress(presign.uploadUrl, file, (p) => {
    opts?.onProgress?.({ ...p, filename });
  });

  const finalized = await finalizePagesUpload(presign.finalizeToken);

  return {
    filename,
    contentType: finalized.upload.mimeType,
    size: finalized.upload.size,
    bucket: finalized.upload.bucket,
    key: finalized.upload.s3Key,
    path: finalized.upload.path,
    uploadId: finalized.upload.uploadId,
    publicUrl: buildUploadedFileUrl({
      bucket: finalized.upload.bucket,
      key: finalized.upload.s3Key,
    }),
    inlineAttachmentMaxBytes:
      finalized.inlineAttachmentMaxBytes ||
      presign.inlineAttachmentMaxBytes ||
      GATEWAY_INLINE_ATTACHMENT_MAX_BYTES,
  };
}

export async function uploadFilesSequential(
  files: Array<{ file: File; displayName?: string }>,
  opts?: {
    onProgress?: (p: UploadProgress) => void;
  },
): Promise<UploadedFileMeta[]> {
  const out: UploadedFileMeta[] = [];
  for (const item of files) {
    const meta = await uploadOneFile(item.file, {
      displayName: item.displayName,
      onProgress: opts?.onProgress,
    });
    out.push(meta);
  }
  return out;
}

function inferAttachmentType(mimeType: string): ChatAttachment['type'] {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('audio/')) return 'audio';
  return 'file';
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Failed to read ${file.name || 'file'} for chat attachment`));
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error(`Failed to encode ${file.name || 'file'} for chat attachment`));
        return;
      }
      const comma = reader.result.indexOf(',');
      resolve(comma >= 0 ? reader.result.slice(comma + 1) : reader.result);
    };
    reader.readAsDataURL(file);
  });
}

export async function buildChatAttachment(
  file: File,
  opts?: { maxBytes?: number; pagesUpload?: UploadedFileMeta },
): Promise<ChatAttachment | null> {
  const maxBytes = getChatAttachmentInlineMaxBytes(opts?.maxBytes);
  if (file.size > maxBytes) {
    return null;
  }

  return {
    type: inferAttachmentType(file.type || 'application/octet-stream'),
    content: await fileToBase64(file),
    mimeType: file.type || 'application/octet-stream',
    fileName: file.name || `upload-${Date.now()}`,
    ...(opts?.pagesUpload
      ? {
          pages: {
            path: opts.pagesUpload.path,
            s3Key: opts.pagesUpload.key,
            bucket: opts.pagesUpload.bucket,
            uploadId: opts.pagesUpload.uploadId,
          },
        }
      : {}),
  };
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (bytes >= 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${bytes} B`;
}

function formatPagesUploadIdentity(meta: UploadedFileMeta): string {
  return [
    `path: ${meta.path}`,
    `s3Key: ${meta.key}`,
    `bucket: ${meta.bucket}`,
    `uploadId: ${meta.uploadId}`,
  ].join(', ');
}

export function buildChatUploadBatchMessage(items: ChatUploadBatchMessageItem[]): string {
  if (!items.length) {
    return '📎 Attached files from Pages';
  }

  if (items.length === 1) {
    const [item] = items;
    const inlineNote = item.attachedInline
      ? ''
      : `, but it was too large to attach inline to this chat (limit ${formatBytes(item.inlineAttachmentMaxBytes)})`;
    return [
      `📎 Attached ${item.filename} from Pages (${item.path})${inlineNote}`,
      item.publicUrl,
    ].filter(Boolean).join('\n');
  }

  const lines = items.flatMap((item) => {
    const inlineNote = item.attachedInline
      ? 'attached inline'
      : `Pages only, inline limit ${formatBytes(item.inlineAttachmentMaxBytes)}`;
    const summary = `• ${item.filename} — ${item.path} (${inlineNote})`;
    return item.publicUrl ? [summary, item.publicUrl] : [summary];
  });

  return [`📎 Attached ${items.length} files from Pages`, ...lines].join('\n');
}

export function buildChatUploadMessage(
  meta: UploadedFileMeta,
  opts?: { attachedInline?: boolean },
): string {
  const objectUrl = meta.publicUrl || buildUploadedFileUrl(meta);

  if (opts?.attachedInline === false) {
    return [
      `📎 Uploaded ${meta.filename} to Pages (${formatPagesUploadIdentity(meta)}), but it was too large to attach inline to this chat (limit ${formatBytes(meta.inlineAttachmentMaxBytes)}).`,
      objectUrl,
    ].join('\n');
  }

  return [
    `📎 Attached ${meta.filename} from Pages (${meta.path})`,
    objectUrl,
  ].join('\n');
}
